import React from 'react';
import TailwindButton from '../../Tailwind/TailwindButton';
import TailwindSelect from '../../Tailwind/TailwindSelect';
import { SalesforceFormData } from '../types';
import { ERP_TYPES } from '../constants';
import SalesforceForm from '../forms/SalesforceForm';

interface AddViewProps {
    newConnectionType: string;
    setNewConnectionType: (type: string) => void;
    salesforceForm: SalesforceFormData;
    setSalesforceForm: React.Dispatch<React.SetStateAction<SalesforceFormData>>;
    privateKeyInputMode: 'text' | 'file';
    setPrivateKeyInputMode: (mode: 'text' | 'file') => void;
    createLoading: boolean;
    saveError: string | null;
    setSaveError: (error: string | null) => void;
    onBack: () => void;
    onCreate: () => void;
}

export default function AddView({
    newConnectionType,
    setNewConnectionType,
    salesforceForm,
    setSalesforceForm,
    privateKeyInputMode,
    setPrivateKeyInputMode,
    createLoading,
    saveError,
    setSaveError,
    onBack,
    onCreate,
}: AddViewProps) {
    const canCreate = () => {
        if (!newConnectionType) return false;
        if (newConnectionType === 'salesforce') {
            return (
                salesforceForm.name &&
                salesforceForm.sf_instance_url &&
                salesforceForm.sf_client_id &&
                salesforceForm.sf_username &&
                salesforceForm.sf_private_key
            );
        }
        return false;
    };

    const erpOptions = Object.entries(ERP_TYPES).map(([key, config]) => ({
        value: key,
        label: config.label,
    }));

    return (
        <div className="space-y-5">
            {/* Back link */}
            <button
                onClick={onBack}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
                <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                Back to list
            </button>

            {saveError && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">error</span>
                        {saveError}
                    </div>
                    <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            )}

            <TailwindSelect
                value={newConnectionType}
                onChange={(val) => setNewConnectionType(val)}
                options={erpOptions}
                label="Connection Type"
                placeholder="Select an ERP type"
            />

            {newConnectionType === 'salesforce' && (
                <SalesforceForm
                    form={salesforceForm}
                    setForm={setSalesforceForm}
                    isEditing={true}
                    privateKeyInputMode={privateKeyInputMode}
                    setPrivateKeyInputMode={setPrivateKeyInputMode}
                    isCreateMode={true}
                />
            )}

            {newConnectionType && (
                <div className="flex justify-end pt-2">
                    <TailwindButton
                        variant="primary"
                        icon={createLoading ? undefined : 'save'}
                        onClick={onCreate}
                        disabled={createLoading || !canCreate()}
                    >
                        {createLoading ? (
                            <span className="flex items-center gap-2">
                                <span className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Creating...
                            </span>
                        ) : (
                            'Create Connection'
                        )}
                    </TailwindButton>
                </div>
            )}
        </div>
    );
}
